import { useTranslation } from 'react-i18next';
import { keycloakStore, useKeycloakStore } from '../../../shared_stores';
import { Button, Modal, Space, Stack, Title, Text, Flex } from '@mantine/core';
import ArrowBackIosNewOutlinedIcon from '@mui/icons-material/ArrowBackIosNewOutlined';
import { MeasurementSelector } from './measurementSelector';
import { CategorySummary, RiskLevel } from './categorySummary';
import { ParameterCards } from './parameterCards';
import { ParameterMeter } from './parameterMeter';
import { useDisclosure } from '@mantine/hooks';
import classes from '../../../app.module.css';
import { RefObject, useEffect, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { MeasurementDataWithIdAndDate, useReportsStore } from '../stores';
import { FormulaUpdateModal, LoadingElement } from '../../../shared_components';
import { observer } from 'mobx-react-lite';
import { dateFormatter } from '../util';
import { DateTime } from 'luxon';
import { ParameterLevel, Parameters } from '@web/common';

export const ReportOverview = observer(() => {
  const { t } = useTranslation(['common', 'reports']);
  const navigate = useNavigate();
  const kcStore = useKeycloakStore();
  const reportsStore = useReportsStore();
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [opened, { open, close }] = useDisclosure(false);
  const [currentReportId, setCurrentReportId] = useState<string>('');
  const [previousReportId, setPreviousReportId] = useState<string>('');
  const [nextReportId, setNextReportId] = useState<string>('');
  const [currentReportData, setCurrentReportData] =
    useState<MeasurementDataWithIdAndDate>();
  const [previousReportData, setPreviousReportData] =
    useState<MeasurementDataWithIdAndDate>();
  const [searchParams, setSearchParams] = useSearchParams();
  const fitnessSection = useRef<HTMLDivElement>(null);
  const heartSection = useRef<HTMLDivElement>(null);
  const riskSection = useRef<HTMLDivElement>(null);

  //TODO CBYN-179: move logic to backend
  /*  These useEffects are repsonsible for setting the correct report ids. The intended behaviour:
      The /reports/overview page can be navigated to either with a searchParameter (id=xxxxxxx) or without.
      If a searchParameter is given, or the given id is invalid, the page should display the results of that report.
      If not it should display the latest report. In addition to setting currentReportId, previousReportId and nextReportId
      also need to be set, to enable the buttons that navigate between reports and the "change since last report" trends.
      As the underlying data structure is an array, checks need to be done if there is a previous and/or next report to prevent an index out of bounds exception.
  */

  useEffect(() => {
    if (searchParams.get('id') !== null) {
      setCurrentReportId(searchParams.get('id') || '');
      setSearchParams();
    }
  }, [setCurrentReportId, searchParams, setSearchParams]);

  useEffect(() => {
    if (reportsStore.loadedMeasurementData && reportsStore.measurements) {
      const index = reportsStore.measurements.findIndex(
        (entry) => entry.id === currentReportId,
      );
      if (index > -1) {
        if (index > 0) {
          setPreviousReportId(reportsStore.measurements[index - 1].id);
        } else {
          setPreviousReportId('');
        }
        if (index + 1 < reportsStore.measurements.length) {
          setNextReportId(reportsStore.measurements[index + 1].id);
        } else {
          setNextReportId('');
        }
      } else if (searchParams.get('id') === null && index === -1) {
        const latestIndex = reportsStore.measurements.length - 1;
        setCurrentReportId(reportsStore.measurements[latestIndex].id);
        if (latestIndex > 0) {
          setPreviousReportId(reportsStore.measurements[latestIndex - 1].id);
        }
      }
    }
  }, [
    reportsStore.loadedMeasurementData,
    reportsStore.measurements,
    currentReportId,
    searchParams,
    setSearchParams,
    reportsStore,
  ]);

  useEffect(() => {
    const measurementData = reportsStore.getMeasurementFromId(currentReportId);
    const previousMeasurementData =
      reportsStore.getMeasurementFromId(previousReportId);
    setCurrentReportData(measurementData);
    setPreviousReportData(previousMeasurementData);
  }, [currentReportId, previousReportId, reportsStore]);

  if (keycloakStore.authenticated) {
    if (!reportsStore.loadedMeasurementData) {
      void reportsStore.loadAllMeasurements(kcStore.token);
      return (
        <Flex
          direction={'column'}
          justify={'center'}
          style={{
            height: '100%',
            width: '100%',
            backgroundColor: 'var(--mantine-color-clinicalWhite-0)',
          }}
        >
          <LoadingElement />
        </Flex>
      );
    }
  }

  const measurementDate = () => {
    const dateString = reportsStore.getMeasurementFromId(currentReportId)?.date;
    if (dateString) {
      const date = DateTime.fromISO(dateString);
      return dateFormatter(date);
    } else return '';
  };

  function handleReportDeletion() {
    //TODO CBYN-136 implement report deletion once endpoints are available
    console.log('deleted Report');
    close();
  }

  function scrollToSection(target: RefObject<HTMLDivElement | null>) {
    if (target.current) {
      target.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }

  return (
    <Flex
      direction={'row'}
      justify={'center'}
      style={{
        width: '100%',
        backgroundColor: 'var(--mantine-color-clinicalWhite-0)',
      }}
    >
      {kcStore.authenticated && <FormulaUpdateModal />}
      <div className={classes.reportOverview}>
        <Modal
          classNames={{ content: classes.modalContentWhite }}
          opened={opened}
          onClose={close}
          title={t('reports:delete_report_title')}
        >
          <Stack>
            <Text>{t('reports:delete_report_message')}</Text>
            <Button
              onClick={() => void handleReportDeletion()}
              style={{
                backgroundColor: 'var(--mantine-color-competenceBlue-0)',
              }}
            >
              {t('reports:delete_report')}
            </Button>
            <Button variant={'outline'} onClick={close}>
              {t('common:cancel')}
            </Button>
          </Stack>
        </Modal>
        {kcStore.authenticated ? (
          <Button
            variant={'list'}
            onClick={() => void navigate('/reports/dashboard')}
            leftSection={
              <ArrowBackIosNewOutlinedIcon
                style={{ fontSize: 'var(--mantine-fontSize-md)' }}
              />
            }
          >
            <b>{t('reports:to_dashboard')}</b>
          </Button>
        ) : (
          <Space h={'var(--mantine-spacing-lg)'} />
        )}
        {kcStore.authenticated && (
          <MeasurementSelector
            measurementDate={measurementDate()}
            isLatest={reportsStore.isLatestMeasurement(currentReportId)}
            onClickLeft={() => setCurrentReportId(previousReportId)}
            hideLeft={previousReportId === ''}
            onClickRight={() => setCurrentReportId(nextReportId)}
            hideRight={nextReportId === ''}
          />
        )}
        <Stack
          gap={'var(--mantine-spacing-xl)'}
          style={{ marginTop: 'var(--mantine-spacing-xl)' }}
          className={classes.reportCategorySummaries}
        >
          <CategorySummary
            title={t('reports:fitness')}
            level={ParameterLevel.HIGH}
            risk={RiskLevel.LOW}
            sectionLink={() => scrollToSection(fitnessSection)}
            imageUrl="/../../../../assets/img/fitness.svg"
          />
          <CategorySummary
            title={t('reports:heart_and_circulatory_system')}
            level={ParameterLevel.LOW}
            risk={RiskLevel.HIGH}
            messages={[
              {
                parameter: t('reports:blood_pressure'),
                level: ParameterLevel.HIGH,
              },
              {
                parameter: t('reports:pulse'),
                level: ParameterLevel.VERY_HIGH,
              },
            ]}
            sectionLink={() => scrollToSection(heartSection)}
            imageUrl="/../../../../assets/img/heart_health_small.svg"
          />
          <Flex className={classes.reportCategorySummariesBottomRow}>
            <CategorySummary
              title={t('reports:risk_and_stress')}
              level={ParameterLevel.MID}
              risk={RiskLevel.MODERATE}
              messages={[
                {
                  parameter: t('reports:stress_level'),
                  level: ParameterLevel.HIGH,
                },
              ]}
              sectionLink={() => scrollToSection(riskSection)}
              imageUrl="/../../../../assets/img/stress.svg"
            />
          </Flex>
          <Stack style={{ width: '100%' }}>
            <Title ref={fitnessSection} order={4}>
              {t('reports:fitness')}
            </Title>
            <ParameterCards
              data={[
                {
                  parameter: Parameters.BODY_WEIGHT,
                  value: currentReportData?.dataSet.BodyWeight.value,
                  unit: currentReportData?.dataSet.BodyWeight.unit,
                  previousValue: previousReportData?.dataSet.BodyWeight.value,
                },
                {
                  parameter: Parameters.FAT_PERCENTAGE,
                  value: currentReportData?.dataSet.FatPercentage.value,
                  unit: currentReportData?.dataSet.FatPercentage.unit,
                  previousValue:
                    previousReportData?.dataSet.FatPercentage.value,
                },
                {
                  parameter: Parameters.MUSCLE_PERCENTAGE,
                  value: currentReportData?.dataSet.MusclePercentage.value,
                  unit: currentReportData?.dataSet.MusclePercentage.unit,
                  previousValue:
                    previousReportData?.dataSet.MusclePercentage.value,
                },
                {
                  parameter: Parameters.BODY_MASS_INDEX,
                  value: currentReportData?.dataSet.BodyMassIndex.value,
                  unit: currentReportData?.dataSet.BodyMassIndex.unit,
                  previousValue:
                    previousReportData?.dataSet.BodyMassIndex.value,
                },
                {
                  parameter: Parameters.PHYSICAL_FITNESS,
                  value: currentReportData?.dataSet.PhysicalFitness.value,
                  unit: currentReportData?.dataSet.PhysicalFitness.unit,
                  previousValue:
                    previousReportData?.dataSet.PhysicalFitness.value,
                },
              ]}
            />
          </Stack>
          <Stack style={{ width: '100%' }}>
            <Title ref={heartSection} order={4}>
              {t('reports:heart_and_circulatory_system')}
            </Title>
            <ParameterCards
              data={[
                {
                  parameter: Parameters.BLOOD_PRESSURE_SYSTOLIC,
                  value: currentReportData?.dataSet.BloodPressureSystolic.value,
                  unit: currentReportData?.dataSet.BloodPressureSystolic.unit,
                  previousValue:
                    previousReportData?.dataSet.BloodPressureSystolic.value,
                },
                {
                  parameter: Parameters.BLOOD_PRESSURE_DIASTOLIC,
                  value:
                    currentReportData?.dataSet.BloodPressureDiastolic.value,
                  unit: currentReportData?.dataSet.BloodPressureDiastolic.unit,
                  previousValue:
                    previousReportData?.dataSet.BloodPressureDiastolic.value,
                },
                {
                  parameter: Parameters.PULSE,
                  value: currentReportData?.dataSet.Pulse.value,
                  unit: currentReportData?.dataSet.Pulse.unit,
                  previousValue: previousReportData?.dataSet.Pulse.value,
                },
              ]}
            />
            <Flex className={classes.parameterMeterRow}>
              <ParameterMeter
                imageUrl="/../../../../assets/img/heart_health.svg"
                parameter={Parameters.CARDIO_FITNESS}
                value={currentReportData?.dataSet.CardioFitness.value}
                thresholds={currentReportData?.dataSet.CardioFitness.thresholds}
              />
              <ParameterMeter
                imageUrl="/../../../../assets/img/heart_strength.svg"
                parameter={Parameters.HEART_STRENGTH}
                value={currentReportData?.dataSet.HeartStrength.value}
                thresholds={currentReportData?.dataSet.HeartStrength.thresholds}
              />
            </Flex>
          </Stack>
          <Space />
          <Stack style={{ width: '100%' }}>
            <Title ref={riskSection} order={4}>
              {t('reports:risk_and_stress')}
            </Title>
            <Flex className={classes.parameterMeterRow}>
              <ParameterMeter
                imageUrl="/../../../../assets/img/heart_risk.svg"
                parameter={Parameters.CARDIOVASCULAR_RISK}
                value={currentReportData?.dataSet.CardiovascularRisk.value}
                thresholds={
                  currentReportData?.dataSet.CardiovascularRisk.thresholds
                }
              />
              <ParameterMeter
                imageUrl="/../../../../assets/img/stress_level.svg"
                parameter={Parameters.STRESS_LEVEL}
                value={currentReportData?.dataSet.StressLevel.value}
                thresholds={currentReportData?.dataSet.StressLevel.thresholds}
              />
            </Flex>
          </Stack>
          {/* 
          TODO CBYN-179: Add feature back in v2

          {kcStore.authenticated && (
            <Flex
              direction={'row'}
              justify={'center'}
              style={{ width: '100%' }}
            >
              <Button variant="text" onClick={open}>
                {t('reports:delete_report')}
              </Button>
            </Flex>
          )} */}
          <Space h={48} />
        </Stack>
      </div>
    </Flex>
  );
});
